import React, { useEffect, useRef } from 'react';
import { Box, Button, Drawer, useMediaQuery, useTheme } from '@mui/material';
import { Link, Route, Routes, useLocation } from 'react-router';

import { getSpinnersToDisplay, SpinnerMessage } from '@paragon/task-spinner';
import { createScrollbarStyling } from '@paragon/themes';

import { useAppSelector } from './hooks';
import { OrganizationSettings } from './organization/OrganizationSettings';
import { InformationCard } from './personal/InformationCard';
import { PersonalSettings } from './personal/PersonalSettings';
import { UserDirectory } from './user/UserDirectory';

const drawerWidth = 300;

const buttonSx = {
  my: 1,
  py: 1,
  width: '100%',
  bgcolor: 'backContrast.main',
  color: 'buttonColor.main',
  border: '.25px solid #b1b1b1',
  '&:hover': {
    color: '#fff',
  },
} as const;

interface Props {
  readonly navOpen: boolean;
  readonly onNavClose: () => void;
}

export default function Content({ navOpen, onNavClose }: Props) {
  const mainRef = useRef<HTMLElement | null>(null);
  const spinnerMessages = useAppSelector(getSpinnersToDisplay);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const location = useLocation();

  useEffect(() => {
    mainRef.current!.scrollTo(0, 0);
  }, [location]);

  useEffect(() => {
    if (!isSmallScreen) {
      onNavClose();
    }
  }, [isSmallScreen, onNavClose]);

  const messagesHtml = Array.from(spinnerMessages).map((text) => <SpinnerMessage text={text} key={text} />);

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        flexGrow: 1,
        overflow: 'auto',
      }}
    >
      <Drawer
        variant={isSmallScreen ? 'temporary' : 'permanent'}
        open={!isSmallScreen || navOpen}
        onClose={onNavClose}
        sx={
          isSmallScreen
            ? { '& .MuiDrawer-paper': { width: drawerWidth, maxWidth: '85vw', backgroundImage: 'none' } }
            : {
                backgroundColor: 'backContrast.dark',
                '& .MuiDrawer-paper': { position: 'relative', width: drawerWidth },
              }
        }
      >
        <InformationCard />
        <Box
          sx={{
            px: 2,
          }}
        >
          <Button component={Link} to="/" sx={buttonSx} variant="contained" onClick={onNavClose}>
            Personal Settings
          </Button>
          <Button component={Link} to="/organization-settings" sx={buttonSx} variant="contained" onClick={onNavClose}>
            Organization Settings
          </Button>
          <Button component={Link} to="/user-directory" sx={buttonSx} variant="contained" onClick={onNavClose}>
            User Directory
          </Button>
        </Box>
      </Drawer>
      <Box
        component="main"
        ref={mainRef}
        sx={[
          {
            flexGrow: 1,
            height: '100%',
            backgroundColor: 'canvas.main',
            minWidth: 0, // So the Typography noWrap works
            overflowX: 'hidden',
            overflowY: 'auto',
          },
          createScrollbarStyling,
        ]}
      >
        {messagesHtml}
        <Routes>
          <Route path="/" element={<PersonalSettings />} />
          <Route path="/organization-settings" element={<OrganizationSettings />} />
          <Route path="/user-directory" element={<UserDirectory />} />
        </Routes>
      </Box>
    </Box>
  );
}
